import * as React from "react";
import { Dispatch } from "redux";
import { connect } from 'react-redux';
import * as H from 'history';
import { RouteComponentProps, Route, Link, withRouter, Redirect, RouteProps, match } from 'react-router-dom';
import { withLocalize, LocalizeContextProps, Translate } from "react-localize-redux";
import { Spin, Alert } from "antd";
import Routes from './Routes';
import bindAllOfThis from "src/utils/BindThisHelper";
import History from 'src/core/ConfigureHistory';

// Layouts
import AdminLayout from "src/layouts/admin/AdminLayout";
import UserLayout from "src/layouts/user/UserLayout";
import PublicLayout from "src/layouts/public/PublicLayout";
import GameLayout from "src/layouts/game/GameLayout";
import SpaceStationLayout from "src/layouts/spacestation/SpaceStationLayout";
import PlanetLayout from "src/layouts/planet/PlanetLayout";
import CleanLayout from "src/layouts/clean/CleanLayout";

import { ReduxStoreModel } from "src/redux/ReduxModel";
import { UserState } from "src/redux/UserReducer";
import UserAction from "src/redux/UserActions";
import { ConstantState } from "src/redux/ConstantReducer";
import { MissionState } from "src/redux/CheckpointReducer";
import DebugUtil from "src/utils/DebugUtil";
import AccountApiController from "src/api/AccountApiController";
import Languages from "src/constants/Languages";
import { Roles } from "src/constants/Roles";
import { Track } from 'src/providers/TelemetryService';

interface LayoutComponentProps extends LocalizeContextProps {
    /* Notes
    * Typescript does not like when you take a parameter of type 'string' and turn it into type 'string | RouteUrl'
    * So thats why this class does not extend RouteProps like i want it to :/
    */

    // Required Props
    /** The path to this component. Think of it as a page location */
    path: string;
    /** The actual react component to render */
    component: React.SFC<RouteComponentProps<any> | undefined> | React.ComponentClass<RouteComponentProps<any> | undefined>;
    /** The layout to render the component in */
    layout: typeof AdminLayout | typeof UserLayout | typeof PublicLayout | typeof GameLayout | typeof SpaceStationLayout | typeof PlanetLayout | typeof CleanLayout;

    // Other props not passed it, but are coming from the context somewhere
    /** Defines if the path is exact or 'close enough' */
    exact?: boolean;
    /** This prop's class is never defined or used anywhere, so it will just be like this */
    computedMatch?: { path: string, url: string, isExact: boolean, params: {} };
    /** Yet another way to get the url. 'Search' contains only the '?foo=bar' raw text from the url */
    location?: H.Location;

    /** Forces the page to render only after checking the user context */
    requireLogin?: boolean;

    // Unused
    children?: ((props: RouteComponentProps<any>) => React.ReactNode) | React.ReactNode;
    strict?: boolean;

    // From Redux
    Mission: MissionState;
    User: UserState;
    Constants: ConstantState;
    softLogin: () => Promise<boolean>;
}

interface LayoutComponentState {
    isLoading: boolean;
    isIntercomStarted: boolean;
}

class LayoutComponent extends React.Component<LayoutComponentProps, LayoutComponentState> {
    interval: NodeJS.Timer = null;
    constructor(props: LayoutComponentProps) {
        super(props);
        bindAllOfThis(this, LayoutComponent.prototype);

        this.state = {
            isLoading: true,
            isIntercomStarted: false
        };

        History.listen((location, action) => {
            if (!this.state.isIntercomStarted) {
                return;
            }

            // Update intercom since we "changed pages". Yay single page apps
            this.updateIntercom();
        });
    }

    componentWillReceiveProps(nextProps: LayoutComponentProps) {
        if (nextProps.activeLanguage !== this.props.activeLanguage) {
            // JB: This will capture all language changes! This is used to set the Languages and TranslateApi with a fresh translate()
            if (DebugUtil.isDebugEnabled()) {
                console.warn("Layout: Language changed");
            }
            // This has to be here, otherwise the translate() function will not have the proper active language
            let language = Languages.FindByCode(nextProps.activeLanguage.code);
            Languages.SetActiveLanguage(language, nextProps.translate);
        }

        // Start and stop intercom
        if (nextProps.User.roleID !== Roles.Student.id) {
            if (nextProps.User.state === "finished" && !this.state.isIntercomStarted) {
                // Start intercom
                this.startIntercom();
                this.setState({ isIntercomStarted: true });
            } else if (nextProps.User.state === "none" && this.state.isIntercomStarted) {
                this.stopIntercom();
                this.setState({ isIntercomStarted: false });
            }
        }
    }

    componentDidMount() {
        // Note to self: Component is only mounted once and never re-mounted in pretty much any situation

        // Start the keep alive. In debug mode because kuder has not indicated otherwise. Might also want to have this be 5 or even 10 minutes
        if (DebugUtil.isDebugEnabled()) {
            // Include some insanity checking
            if (this.interval != null) {
                clearInterval(this.interval);
            }
            this.interval = setInterval(() => {
                if (this.props.User.state === "failed") {
                    // Nothing to KeepAlive, skip
                    return;
                }
                // No need to log a success and failures just redirect automagically to login
                AccountApiController.keepAlive();
            }, 180000);
        }

        if (this.props.User.state === "none") {
            this.props.softLogin().then(result => {
                // Set the users language
                if (this.props.User.cultureCode != null) {
                    // JB: This will set the language when this component is first rendered and not after that
                    // JB: This is hit on refresh and not on the login page, since it handles its own stuff
                    let language = Languages.FindByCode(this.props.User.cultureCode);
                    this.props.setActiveLanguage(language.code);
                }
                this.setState({
                    isLoading: false
                });
            });
        } else {
            this.setState({
                isLoading: false
            });
        }
    }

    private getIntercomUserData() {
        let isEmulating = this.props.User.isEmulating;
        let roleId = isEmulating ? this.props.User.emulationInfo?.roleId : this.props.User?.roleID;
        let userRole = Roles.FindById(roleId);
        let isKuderAdmin = userRole === Roles.KuderAdmin;

        let clientId = isKuderAdmin ? 0 : isEmulating ? this.props.User.emulationInfo?.clientId : this.props.User.clientId;
        let clientName = isKuderAdmin ? "N/A" : isEmulating ? this.props.User.emulationInfo?.clientName : this.props.User.clientName;
        let schoolLevel = this.props.User.schoolName;
        let districtName = this.props.User.districtName;
        let stateName = this.props.User.stateName;

        return {
            app_id: "u0ob3l49",
            name: this.props.User.firstName + " " + this.props.User.lastName,
            email: this.props.User.email, // Email address
            // created_at: Date.now(), // Signup date as a Unix timestamp
            user_id: this.props.User.userId,
            School_Level: schoolLevel,
            District_Level: districtName,
            State_Level: stateName,
            application: "Galaxy",
            company: {
                id: clientId,
                name: clientName
            },
            user_role: userRole.display
        };
    }

    private startIntercom() {
        let w = window as any;
        let userData = this.getIntercomUserData();
        w.Intercom('boot', userData);
        w.Intercom('onShow', () => {
            Track.OpenIntercom(this.props.User.userId);
        });
    }

    private updateIntercom() {
        let w = window as any;
        let userData = this.getIntercomUserData();
        w.Intercom('update', userData);
    }

    private stopIntercom() {
        let w = window as any;
        w.Intercom('shutdown');
    }

    getComponent(routerContext: RouteComponentProps<{}>) {
        let spinner = <Spin key="fancySpinnerOfDoom" className={"centered very-large-spinner"} />;
        if (this.state.isLoading) {
            return spinner;
        }
        if (this.props.requireLogin) {
            if (this.props.User.state === "failed" || this.props.Constants.state === "error") {
                return <Redirect push to={Routes.LOGIN} from={routerContext.location.pathname} />;
            } else if (this.props.User.state === "none" || this.props.User.state === "loading") {
                // JB (12/7/18): Removed other redux checks as login will now load all before switching to complete
                return spinner;
            }
        }
        return (
            <this.props.layout {...routerContext}>
                <this.props.component {...routerContext} key={location.href} />
            </this.props.layout>
        );
    }

    render() {
        return <Route key="routeKey" exact path={this.props.path} render={(routerContext) => this.getComponent(routerContext)} />;
    }
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return ({
        softLogin: () => UserAction.SoftLogin(dispatch),
    });
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User,
        Constants: state.Constants,
        Mission: state.Mission,
    };
}

export default withLocalize(connect(mapStateToProps, mapDispatchToProps)(LayoutComponent));



// WEBPACK FOOTER //
// ./src/core/LayoutComponent.tsx